import { Injectable, inject } from '@angular/core';
import { explainedInPlace, rethrowAsRefusal } from '@core/helpers/installation-refusal.helper';
import {
  Installation,
  InstallationChecklist,
  MeasurementRole,
  MeasurementRoles,
  Preset,
  Presets,
} from '@core/models/installation.model';
import { UpdateProfile } from '@core/models/installation-write.model';
import { ApiService } from '@core/services/api.service';
import { Observable, catchError, map, shareReplay } from 'rxjs';

// Every request fails with an InstallationRefusal.
@Injectable({
  providedIn: 'root',
})
export class InstallationProfileService {
  private readonly apiService = inject(ApiService);

  // The role catalogue and the presets change only with a release, so each is read once. A failed
  // read is not kept: the next caller asks again.
  private readonly roles$ = this.read<MeasurementRoles>('/installations/roles').pipe(
    map(response => response.roles),
    shareReplay(1),
  );
  private readonly presets$ = this.read<Presets>('/installations/presets').pipe(
    map(response => response.presets),
    shareReplay(1),
  );

  roles(): Observable<MeasurementRole[]> {
    return this.roles$;
  }

  presets(): Observable<Preset[]> {
    return this.presets$;
  }

  checklist(installationOid: string): Observable<InstallationChecklist> {
    return this.read<InstallationChecklist>(`/installations/${installationOid}/checklist`);
  }

  // The answer carries no points, so a caller re-reads the installation after a save.
  update(installationOid: string, body: UpdateProfile): Observable<Installation> {
    return this.apiService
      .putRaw<Installation>(`/installations/${installationOid}/profile`, body, explainedInPlace())
      .pipe(catchError(rethrowAsRefusal));
  }

  private read<T>(path: string): Observable<T> {
    return this.apiService
      .getRaw<T>(path, {}, explainedInPlace())
      .pipe(catchError(rethrowAsRefusal));
  }
}
